import { Pages } from 'constants/menu';
import { AddSpecialConditionButton } from './modals/specialConditions/AddSpecialConditionButton';
import { useSelectedContract } from 'hooks';
import {
  FormCondition,
  SpecialConditionData,
  StaffSpecialConditionsResponse,
  useConstructConditionClauseText,
  useInvalidateUseStaffGetSpecialConditions,
  useStaffGetSpecialConditions,
  useStaffUpdateContractSpecialConditions,
} from '@urbanx/agx.campaigns.client';
import { AgxEmptyState, useToast } from '@urbanx/agx.ui-components2';
import { useQueryClient } from '@tanstack/react-query';
import SpecialConditionCollapsiblePanel from './panels/SpecialConditionCollapsiblePanel';
import { RexDisconnectedModalFields } from 'pages/campaigns/components/manageCampaign/RexDataUpdateHelper';
import { useGetListingByCampaignId } from '@urbanx/agx.rex.client';
import { isEqual } from 'lodash-es';
import useRexDisconnectedModal from 'pages/campaigns/components/manageCampaign/useRexDisconnectedModal';
import {
  isActionableSpecialCondition,
  withUpdatedSpecialConditionDueDate,
} from './specialConditionDueDate';
import { prepareCustomSpecialConditionsForSave } from './modals/specialConditions/customSpecialConditionEditor';
import { ContractInformationPage } from './ContractInformationPage';
import { useContractInformationEditor } from './useContractInformationEditor';

const SpecialConditions = () => {
  const { contract, campaignId } = useSelectedContract();

  const {
    data: specialConditionsResponse,
    isPending,
    isError,
  } = useStaffGetSpecialConditions(campaignId ?? '', contract?.contractId ?? '');
  const invalidateStaffGetSpecialConditions = useInvalidateUseStaffGetSpecialConditions();
  const queryClient = useQueryClient();

  const { modal: rexDisconnectedModal, openWithChanges } = useRexDisconnectedModal();

  const { pushToast } = useToast();

  const { constructConditionClauseText } = useConstructConditionClauseText({
    onSuccess: () => {},
    onError: () => {},
  });

  const { data: rexListing, isPending: isPendingRexListing } = useGetListingByCampaignId(
    campaignId || '',
    campaignId !== undefined
  );

  const { staffUpdateContractSpecialConditions } = useStaffUpdateContractSpecialConditions({
    onSuccess: () => {},
    onError: () => {},
  });

  type SpecialConditionsDraft = {
    conditions: SpecialConditionData[];
    renderedConditions: NonNullable<StaffSpecialConditionsResponse['renderedConditions']>;
  };

  const toDraft = (response: StaffSpecialConditionsResponse): SpecialConditionsDraft => ({
    conditions: response.specialConditionsData ?? [],
    renderedConditions: response.renderedConditions ?? [],
  });

  const source: SpecialConditionsDraft | undefined = specialConditionsResponse
    ? toDraft(specialConditionsResponse)
    : undefined;

  const editor = useContractInformationEditor({
    resourceKey: `${campaignId ?? ''}:${contract?.contractId ?? ''}:special-conditions`,
    source,
    isPending,
    isError,
    persist: async ({ baseline, draft }) => {
      const conditions = prepareCustomSpecialConditionsForSave(draft.conditions);
      const response = await staffUpdateContractSpecialConditions({
        contractId: contract!.contractId,
        body: conditions,
      });

      if (!response?.success) return { accepted: false, errors: response?.errors ?? [] };
      if (!response.specialConditions) throw new Error('Canonical special conditions are required');

      const canonicalSpecialConditions: StaffSpecialConditionsResponse = {
        ...response.specialConditions,
        specialConditionsData: response.specialConditions.specialConditionsData ?? [],
        specialConditionIdToClauseNumberMap:
          response.specialConditions.specialConditionIdToClauseNumberMap ?? {},
        renderedConditions: response.specialConditions.renderedConditions ?? [],
      };

      queryClient.setQueryData<StaffSpecialConditionsResponse>(
        ['staffGetSpecialConditions', campaignId!, contract!.contractId],
        canonicalSpecialConditions
      );

      if (!rexListing && isPendingRexListing === false) {
        openWithChanges(
          !isEqual(conditions, baseline.conditions)
            ? [
                {
                  field: RexDisconnectedModalFields.ExpectedUnconditionalDate,
                  value: '',
                },
              ]
            : []
        );
      }

      return { accepted: true, baseline: toDraft(canonicalSpecialConditions) };
    },
    refresh: {
      authority: 'mutation',
      refreshSource: () => invalidateStaffGetSpecialConditions(campaignId!, contract!.contractId),
    },
  });

  if (!editor.draft) {
    return (
      <ContractInformationPage currentPage={Pages.SPECIAL_CONDITIONS} editor={editor}>
        {null}
      </ContractInformationPage>
    );
  }

  const { conditions: localSpecialConditions, renderedConditions } = editor.draft;

  const onConditionDeleted = (conditionId: string) => {
    if (!conditionId) return;

    const updatedConditions = localSpecialConditions.filter(
      (condition) => condition.id !== conditionId
    );

    editor.update((current) => ({
      conditions: updatedConditions,
      renderedConditions: current.renderedConditions.filter(
        (condition) => condition.id !== conditionId
      ),
    }));
  };

  const onAddCustomCondition = async (customSpecialCondition: SpecialConditionData) => {
    const updatedConditions = prepareCustomSpecialConditionsForSave([
      ...localSpecialConditions,
      customSpecialCondition,
    ]);

    try {
      const rendered = await constructConditionClauseText({
        campaignId: campaignId ?? '',
        contractId: contract?.contractId ?? '',
        model: updatedConditions,
      });
      editor.update(() => ({ conditions: updatedConditions, renderedConditions: rendered }));
      return true;
    } catch {
      pushToast({
        message: 'Failed to construct condition text blocks',
        type: 'error',
      });
      return false;
    }
  };

  const onDueDateUpdated = async (
    conditionId: string,
    clauseId: string,
    newDueDateCondition: FormCondition | undefined,
    newDueDate: string | undefined
  ) => {
    const updatedConditions = prepareCustomSpecialConditionsForSave(
      localSpecialConditions.map((condition) => {
        if (condition.id !== conditionId) return condition;

        return withUpdatedSpecialConditionDueDate(condition, clauseId, newDueDateCondition);
      })
    );

    editor.update((current) => ({
      conditions: updatedConditions,
      renderedConditions: current.renderedConditions.map((renderedCondition) => {
        if (renderedCondition.id !== conditionId) return renderedCondition;

        return {
          ...renderedCondition,
          directDueDate: conditionId === clauseId ? newDueDate : renderedCondition.directDueDate,
          clauses: renderedCondition.clauses?.map((clause) => {
            if (clause.clauseId !== clauseId) return clause;

            return {
              ...clause,
              dueDate: newDueDate,
            };
          }),
        };
      }),
    }));
  };

  const visibleRenderedSpecialConditions = renderedConditions.filter(isActionableSpecialCondition);

  return (
    <ContractInformationPage currentPage={Pages.SPECIAL_CONDITIONS} editor={editor}>
      {visibleRenderedSpecialConditions.length === 0 ? (
        <AgxEmptyState variant="minimal" label="No special conditions added." />
      ) : null}

      {visibleRenderedSpecialConditions.map((renderedCondition) => (
        <SpecialConditionCollapsiblePanel
          renderedSpecialCondition={renderedCondition}
          specialCondition={localSpecialConditions.find(
            (condition) => condition.id === renderedCondition.id
          )}
          onDeleteCondition={onConditionDeleted}
          key={renderedCondition.id ?? renderedCondition.conditionType}
          campaignId={campaignId ?? ''}
          contractId={contract?.contractId ?? ''}
          onDueDateUpdated={onDueDateUpdated}
        />
      ))}
      <AddSpecialConditionButton
        key="add-special-condition-button"
        id="add-special-condition-button"
        label="Add Special Conditions"
        existingConditions={localSpecialConditions}
        renderedConditions={renderedConditions}
        onAddSpecialCondition={onAddCustomCondition}
        campaignId={campaignId ?? ''}
      />
      {rexDisconnectedModal}
    </ContractInformationPage>
  );
};

export default SpecialConditions;
